import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../context/auth';
import { useNavigate } from 'react-router-dom';
import "../cssStyle/PercentageCard.css";

function PercentageCard() {
  const [showToggler, setShowToggler] = useState(true);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [groupedData, setGroupedData] = useState({
    left: [],
    right: []
  });
  // Stats for left and right sections
  const [leftStats, setLeftStats] = useState({ count: 0, average: 0 });
  const [rightStats, setRightStats] = useState({ count: 0, average: 0 });
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // ✅ Reference for interval
  const intervalRef = useRef(null);
  
  const { getAdminAuthToken } = useAuth();
  const navigate = useNavigate();
  const token = getAdminAuthToken();

  // Format percentage function
  const formatPercentage = (percentage) => {
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage.toFixed(2)}%`;
  };

  const formatPrice = (price) => {
    return `₹${price.toFixed(2)}`;
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const date = new Date(time);
    return date.toLocaleString();
  };

  // ✅ Fetch data function
  const fetchData = async (showToast = false) => {
    if (!token) {
      setLoading(false);
      if (showToast) toast.error("Please login first");
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    try {
      const response = await axios.get(
        "https://api.algocontroller.com/admin_app/getting_admin_analysis",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      const rawData = response.data?.data || [];
      setApiData(rawData);
      processDataByCategory(rawData);
      setLastUpdated(new Date());
      setLoading(false);
      
      // ✅ Show toast only on manual refresh
      if (showToast) {
        toast.info(`Data updated!`, {
          position: "top-right",
          autoClose: 2000,
          style: { fontSize: "12px" }
        });
      }
    } catch (err) {
      console.error("API Error:", err);
      if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        setTimeout(() => navigate('/login'), 2000);
      } else if (showToast) {
        toast.error("Failed to fetch data");
      }
      setLoading(false);
    }
  };

  const processDataByCategory = (rawData) => {
    // ✅ GP category for LEFT (Positive/UPPER RANGE)
    const leftCategories = ['GP'];
    // ✅ GD category for RIGHT (Negative/DOWN RANGE)
    const rightCategories = ['GD'];
    
    let leftItems = [];
    let rightItems = [];

    rawData.forEach(insItem => {
      if (insItem.data && Array.isArray(insItem.data)) {
        insItem.data.forEach(item => {
          // ✅ Filter only 'first' type data
          if (item.type !== 'first') return;
          
          const buttonData = {
            symbol: item.symbol,
            price: item.price,
            percentage: item.percentage,
            cat: item.cat,
            type: parseFloat(item.percentage) >= 0 ? "green" : "red",
            time: item.time,
            ins_id: item.ins_id,
            prev_row_close: item.prev_row_close
          };

          if (leftCategories.includes(item.cat)) {
            leftItems.push(buttonData);
          } else if (rightCategories.includes(item.cat)) {
            rightItems.push(buttonData);
          }
        });
      }
    });

    // Remove duplicates based on symbol
    leftItems = removeDuplicates(leftItems);
    rightItems = removeDuplicates(rightItems);

    // SORTING: Descending order (higher percentage first) for GP
    leftItems.sort((a, b) => b.percentage - a.percentage);
    // SORTING: Ascending order (more negative first) for GD
    rightItems.sort((a, b) => a.percentage - b.percentage);

    // Calculate stats for LEFT section (GP)
    const leftCount = leftItems.length;
    const leftAvg = leftCount > 0 
      ? leftItems.reduce((sum, item) => sum + item.percentage, 0) / leftCount 
      : 0;
    
    // Calculate stats for RIGHT section (GD)
    const rightCount = rightItems.length;
    const rightAvg = rightCount > 0 
      ? rightItems.reduce((sum, item) => sum + item.percentage, 0) / rightCount 
      : 0;

    setLeftStats({ count: leftCount, average: leftAvg });
    setRightStats({ count: rightCount, average: rightAvg });
    setGroupedData({ left: leftItems, right: rightItems });
  };

  const removeDuplicates = (items) => {
    const uniqueMap = new Map();
    items.forEach(item => {
      const key = `${item.symbol}_${item.cat}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, item);
      }
    });
    return Array.from(uniqueMap.values());
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`, {
        position: "top-right",
        autoClose: 1000,
        style: { fontSize: "12px", padding: "6px 12px" }
      });
    } catch (err) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.info(`${label} copied!`);
    }
  };

  // ✅ Manual refresh handler
  const handleManualRefresh = () => {
    fetchData(true);
  };

  // SMART TOOLTIP POSITIONING
  const handleMouseEnter = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let leftPos = rect.right + 10;
    let topPos = rect.top;
    
    const tooltipWidth = 230;
    const tooltipHeight = 210;
    
    if (leftPos + tooltipWidth > viewportWidth) {
      leftPos = rect.left - tooltipWidth - 10;
    }
    
    if (leftPos < 10) {
      leftPos = 10;
    } 
    
    if (topPos + tooltipHeight > viewportHeight) {
      topPos = viewportHeight - tooltipHeight - 10;
    }
    
    if (topPos < 10) {
      topPos = 10;
    }
    
    setHoverPosition({
      x: leftPos,
      y: topPos
    });
    setHoveredItem(item);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  // ✅ Initial fetch and set interval for every 10 seconds
  useEffect(() => {
    if (!token) {
      setLoading(false);
      toast.error("Please login first");
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    
    // Initial fetch
    fetchData(false);
    
    // ✅ Set interval to fetch every 10 seconds (10000 ms)
    intervalRef.current = setInterval(() => {
      console.log("Auto-refreshing data...", new Date().toLocaleTimeString());
      fetchData(false); // Silent refresh (no toast)
    }, 10000); // 10 seconds
    
    // ✅ Cleanup interval on component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [token]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading market data...</p>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      
      <div className="percentage-card">
        <div
          onClick={() => setShowToggler(!showToggler)}
          className="card-header"
        >
          <div>
            <h3>First Session</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* ✅ Manual Refresh Button */}
            <span>
              {showToggler ? <IoIosArrowUp /> : <IoIosArrowDown />}
            </span>
          </div>
        </div>

        {showToggler && (
          <div className="cards-container">
            {/* LEFT SECTION - GP (Gross Profit / UPPER RANGE) */}
            <div className="card-left">
              <div className="card-title-left">
                <h4> GP  
                  <span className="stats-count"> ({leftStats.count} Items)</span>
                  <span className="stats-avg"> | Avg: {formatPercentage(leftStats.average)}</span>
                </h4>
              </div>
              
              <div className="chips-grid">
                {groupedData.left.length === 0 ? (
                  <div className="no-data">No GP data available</div>
                ) : (
                  groupedData.left.map((btn, idx) => (
                    <button
                      key={idx}
                      className={`tiny-chip ${btn.type}`}
                      onClick={() => copyToClipboard(btn.symbol, btn.symbol)}
                      onMouseEnter={(e) => handleMouseEnter(e, btn)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <span className="chip-symbol">{btn.symbol}</span>
                      <span className="chip-sep">|</span>
                      <span className={`chip-percent ${btn.type}`}>
                        {formatPercentage(btn.percentage)}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* RIGHT SECTION - GD (Gross Drawdown / DOWN RANGE) */}
            <div className="card-right">
              <div className="card-title-right">
                <h4>GD 
                  <span className="stats-count"> ({rightStats.count} Items)</span>
                  <span className="stats-avg"> | Avg: {formatPercentage(rightStats.average)}</span>
                </h4>
              </div>
              
              <div className="chips-grid">
                {groupedData.right.length === 0 ? (
                  <div className="no-data">No GD data available</div>
                ) : (
                  groupedData.right.map((btn, idx) => (
                    <button
                      key={idx}
                      className={`tiny-chip ${btn.type}`}
                      onClick={() => copyToClipboard(btn.symbol, btn.symbol)}
                      onMouseEnter={(e) => handleMouseEnter(e, btn)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <span className="chip-symbol">{btn.symbol}</span>
                      <span className="chip-sep">|</span>
                      <span className={`chip-percent ${btn.type}`}>
                        {formatPercentage(btn.percentage)}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Tooltip */}
      {hoveredItem && (
        <div
          className="custom-tooltip"
          style={{
            position: 'fixed',
            left: `${hoverPosition.x}px`,
            top: `${hoverPosition.y}px`,
            zIndex: 9999,
          }}
        >
          <div className="tooltip-content">
            <div className="tooltip-row">
              <span className="tooltip-label">Symbol:</span>
              <span className="tooltip-value">{hoveredItem.symbol}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Price:</span>
              <span className="tooltip-value">{formatPrice(hoveredItem.price)}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Change:</span>
              <span className={`tooltip-value ${hoveredItem.type}`}>
                {formatPercentage(hoveredItem.percentage)}
              </span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Category:</span>
              <span className="tooltip-value">{hoveredItem.cat}</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Time:</span>
              <span className="tooltip-value">{formatTime(hoveredItem.time)}</span>
            </div>
            <div className="tooltip-divider"></div>
            <div className="tooltip-footer">
              ✨ Click to copy symbol
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PercentageCard;